import React from 'react';
import {
  Typography,
  Box,
  Divider,
  Grid,
  Autocomplete,
  TextField,
  Checkbox,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

// Personel için tip tanımlaması
export interface Personnel {
  id: string;
  name: string;
  timestamp?: number;
}

// Personel izinleri tipi
export interface PersonnelPermissions {
  changePrice: boolean;
  applyDiscount: boolean;
  cancel: boolean;
  viewReports: boolean;
  warehouseOperations: boolean;
  viewAccountBalance?: boolean;
  listAccounts?: boolean;
  canQuickAddItem?: boolean;
  canAddAccount?: boolean;
}

interface PersonnelSettingsTabProps {
  allPersonnel: Personnel[];
  selectedPersonnelIds: string[];
  selectedPersonnelForPermissions: string | null;
  personnelPermissionsMap: { [personnelId: string]: PersonnelPermissions };
  loading: boolean;
  onPersonnelSelectionChange: (newSelectedIds: string[]) => void;
  onSelectPersonnelForPermissions: (personnelId: string) => void;
  onPermissionChange: (personnelId: string, permission: string, value: boolean) => void;
}

const PersonnelSettingsTab: React.FC<PersonnelSettingsTabProps> = ({
  allPersonnel,
  selectedPersonnelIds,
  selectedPersonnelForPermissions,
  personnelPermissionsMap,
  loading,
  onPersonnelSelectionChange,
  onSelectPersonnelForPermissions,
  onPermissionChange
}) => {
  const { t } = useTranslation('salesSystem');

  return (
    <Paper elevation={0} sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" gutterBottom>{t('personnelSettingsTab.title')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {t('personnelSettingsTab.description')}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {allPersonnel.length > 0 ? (
        <Grid container spacing={2}>
          {/* Sol Taraf: Personel Arama ve Listeleme */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {t('personnelSettingsTab.personnelSelection')}
            </Typography>
            <Autocomplete
              multiple
              size="small"
              id="assigned-personnel-autocomplete"
              options={allPersonnel}
              getOptionLabel={(option) => option.name}
              value={allPersonnel.filter(p => selectedPersonnelIds.includes(p.id))}
              onChange={(event, newValue) => {
                const newSelectedIds = newValue.map(p => p.id);
                onPersonnelSelectionChange(newSelectedIds);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('personnelSettingsTab.searchSelectPersonnel')}
                  fullWidth
                />
              )}
              renderTags={() => null} // Prevent default tags, using list below
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontSize: '0.875rem' }}>
                  <Checkbox
                    size="small"
                    style={{ marginRight: 4 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              )}
              loading={loading}
              sx={{ mb: 2 }}
            />

            {selectedPersonnelIds.length > 0 && (
              <Box>
                <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'medium' }}>{t('personnelSettingsTab.selectedPersonnel')}</Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 400,
                    overflowY: 'auto'
                  }}
                >
                  <List dense disablePadding>
                    {allPersonnel
                      .filter(p => selectedPersonnelIds.includes(p.id))
                      .map(p => (
                        <ListItem
                          key={p.id}
                          selected={selectedPersonnelForPermissions === p.id}
                          button
                          onClick={() => onSelectPersonnelForPermissions(p.id)}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newSelectedIds = selectedPersonnelIds.filter(id => id !== p.id);
                                onPersonnelSelectionChange(newSelectedIds);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                          divider
                        >
                          <ListItemText primary={p.name} />
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              </Box>
            )}
          </Grid>

          {/* Sağ Taraf: Yetki Ayarları */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {t('personnelSettingsTab.permissionSettings')}
            </Typography>

            {selectedPersonnelForPermissions ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {allPersonnel.find(p => p.id === selectedPersonnelForPermissions)?.name || t('personnelSettingsTab.selectedPersonnelFallback')}
                </Typography>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.changePrice || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'changePrice', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.changePrice')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.applyDiscount || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'applyDiscount', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.applyDiscount')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.cancel || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'cancel', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.cancel')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewReports || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'viewReports', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.viewReports')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.warehouseOperations || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'warehouseOperations', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.warehouseOperations')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewAccountBalance || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'viewAccountBalance', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.viewAccountBalance')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.listAccounts || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'listAccounts', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.listAccounts')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.canQuickAddItem || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'canQuickAddItem', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.canQuickAddItem')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.canAddAccount || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'canAddAccount', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.canAddAccount')}
                  />
                </FormGroup>
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  minHeight: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.02)'
                }}
              >
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('personnelSettingsTab.selectPersonnelHint')}
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      ) : (
        <Typography variant="caption">{t('personnelSettingsTab.noPersonnel')}</Typography>
      )}
    </Paper>
  );
};

export default PersonnelSettingsTab; 